import EditIcon from '@mui/icons-material/Edit';
import { sv } from 'date-fns/locale';
import { ReactElement, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useUserDetailsQuery, useUserProfileFieldsQuery } from 'api';
import { GoBackButtonWithTitle, NewConfirmDialog, RichEditor, Spinner } from 'components';
import { DEACTIVATE_USER_COMMENT_MAX_LENGTH } from 'constants/fieldsLength';
import { CreateButton } from 'features/documentCategories/components/DocumentCategories/styled';
import { useModal, useAuth, useSessionState } from 'hooks';
import { theme } from 'styles';
import { Language, UserStatus } from 'types';
import { getFullName } from 'utils';

import { EditEmployeeForm, ExtendedDetailsInfoBlock } from '../../components';
import { ExtendedDetailsAdapter } from './adapter';
import { useEmployeeExtendedDetails } from './hooks';
import { Column, ColumnsW, ContentW, PageHeader, PageW, RichEditorW, SectionTitle } from './styled';

interface LocationState {
  showLicenses?: boolean;
  showEquipment?: boolean;
}

export function EmployeeExtendedDetailsPage(): ReactElement {
  const { employeeId = '' } = useParams<string>();

  const { state = {} } = useLocation();
  const navigate = useNavigate();

  const licensesRef = useRef<HTMLDivElement>(null);
  const equipmentRef = useRef<HTMLDivElement>(null);

  const { isManager, user: currentUser } = useAuth();
  const { t } = useTranslation();
  const { isLoading, user, refetch } = useUserDetailsQuery({ employeeId });
  const [sessionState] = useSessionState('i18nextLng');
  const isSwedish = sessionState === Language.swedish;
  const locales = isSwedish ? sv : undefined;

  const { handleCloseModal: handleCloseEditModal, isOpen, handleOpenModal } = useModal();

  const {
    isOpenModal,
    setDeletedComment,
    handleCloseModal,
    deletedCommentError,
    onChangeEmployeeStatus,
    onHandleOpenChangeStatusModal,
    onHandleFocusTextArea,
  } = useEmployeeExtendedDetails(user?.id || '', user?.status);

  const { isLoading: isUserProfileOrderListLoading, userProfileFields = [] } =
    useUserProfileFieldsQuery({});

  const showLicenses = (state as LocationState)?.showLicenses;
  const showEquipment = (state as LocationState)?.showEquipment;

  useEffect(() => {
    if (licensesRef.current && showLicenses) {
      licensesRef.current.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    }
  }, [showLicenses]);

  useEffect(() => {
    if (equipmentRef.current && showEquipment) {
      equipmentRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
    }
  }, [showEquipment]);

  if (isLoading || isUserProfileOrderListLoading) {
    return <Spinner />;
  }

  if (!user) {
    return <></>;
  }

  const isCurrentUser = currentUser?.id === user?.id;

  const { activationStatus, emergencyContact, privateInformation, publicInfo } =
    ExtendedDetailsAdapter({
      user,
      onHandleOpenChangeStatusModal,
      isManager,
      isCurrentUser,
      locales,
      userProfileFields,
    });

  const userFullName = getFullName({ firstName: user.firstName, lastName: user.lastName });

  const confirmDialogTitle = t(
    user.status === UserStatus.Active
      ? 'profile.extended.modal.offboard'
      : 'profile.extended.modal.activate',
    { userFullName },
  );

  return (
    <PageW>
      <GoBackButtonWithTitle
        title={t('common.back')}
        onClick={() => navigate(-1)}
        containerSx={{
          marginTop: 0,
          maxWidth: '100%',
          paddingBottom: '8px',
        }}
      />
      {!isOpen && (
        <ContentW>
          <PageHeader>
            <SectionTitle>{t('profile.edit.form.title')}</SectionTitle>
            <CreateButton
              startIcon={<EditIcon style={{ fill: theme.palette.common.white }} />}
              onClick={handleOpenModal}
              style={{
                height: '56px',
                padding: '16px',
                backgroundColor: theme.palette.info.main,
                color: theme.palette.common.white,
                borderRadius: '8px',
                fontWeight: 400,
                textTransform: 'none',
              }}
            >
              {t('profile.edit.profile')}
            </CreateButton>
          </PageHeader>

          <ColumnsW>
            <Column>
              <ExtendedDetailsInfoBlock
                title={t('profile.edit.form.publicInfo.title')}
                data={publicInfo}
              />
              {isCurrentUser && isManager ? null : (
                <ExtendedDetailsInfoBlock
                  title={t('profile.extended.activationStatus.title')}
                  data={activationStatus}
                />
              )}
            </Column>

            <Column>
              <ExtendedDetailsInfoBlock
                title={t('profile.edit.form.privateInfo.title')}
                data={privateInformation}
              />
              <ExtendedDetailsInfoBlock
                title={t('profile.edit.form.emergency.title')}
                data={emergencyContact}
              />
            </Column>
          </ColumnsW>
        </ContentW>
      )}

      {isOpen && (
        <EditEmployeeForm
          open={isOpen}
          onClose={handleCloseEditModal}
          employee={user}
          refetchUserDetails={refetch}
          formTitle={t('profile.edit.form.title')}
        />
      )}
      <NewConfirmDialog
        title={confirmDialogTitle}
        subTitle={t('profile.extended.modal.exit')}
        open={isOpenModal}
        onClose={handleCloseModal}
        onConfirm={onChangeEmployeeStatus}
        isLoading={false}
        submitLabel={t('profile.extended.modal.offboardSuccessButton')}
        cancelLabel={t('profile.extended.modal.cancelButton')}
      >
        <RichEditorW>
          <RichEditor
            placeholder={t('profile.extended.modal.placeholder')}
            maxLength={DEACTIVATE_USER_COMMENT_MAX_LENGTH}
            errorText={deletedCommentError}
            onChange={setDeletedComment}
            onFocus={onHandleFocusTextArea}
            onBlur={onHandleFocusTextArea}
            isModal
          />
        </RichEditorW>
      </NewConfirmDialog>
    </PageW>
  );
}
